import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button, IconButton,
  Autocomplete, Drawer, Divider, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaymentIcon from '@mui/icons-material/Payment';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { apiFetch } from '../api';
import { useAuth } from './AuthContext';

const PrintHeader = ({ tenant, branch, supplier, startDate, endDate }) => (
  <Box className="print-only" sx={{ mb: 3, textAlign: 'center', borderBottom: '2px solid black', pb: 2 }}>
    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{tenant?.company_name}</Typography>
    <Typography variant="body1">{branch?.name && `ब्रांच: ${branch.name}`}</Typography>
    <Typography variant="body2">{tenant?.address}</Typography>
    <Divider sx={{ my: 1, borderColor: 'black' }} />
    <Typography variant="h5" sx={{ fontWeight: 'bold', my: 1 }}>व्यापारी खाते उतारा (Supplier Statement)</Typography>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, px: 2, textAlign: 'left' }}>
      <Box>
        <Typography><strong>व्यापारी:</strong> {supplier?.name}</Typography>
        <Typography><strong>मोबाईल:</strong> {supplier?.mobile}</Typography>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography><strong>दिनांक:</strong> {new Date().toLocaleDateString('en-GB')}</Typography>
        <Typography><strong>कालावधी:</strong> {startDate} ते {endDate}</Typography>
      </Box>
    </Box>
  </Box>
);

const getTodayDate = () => new Date().toISOString().split('T')[0];

const SupplierTransaction = () => {
  const { activeBranch, tenant } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);

  const [supplier, setSupplier] = useState(null);
  const [statementData, setStatementData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({
    date: getTodayDate(),
    amount: '',
    payment_method: 'cash',
    account_id: '',
    remark: ''
  });

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(getTodayDate());

  useEffect(() => {
    const fetchBaseData = async () => {
      try {
        const [supRes, accRes] = await Promise.all([
          apiFetch('/api/suppliers'),
          apiFetch('/api/accounts')
        ]);
        if (supRes.ok) setSuppliers(await supRes.json());
        if (accRes.ok) {
          const allAccs = await accRes.json();
          const banks = allAccs.filter(acc => acc.group?.name === 'बँक खाते');
          const cash = allAccs.filter(acc => acc.group?.name === 'रोख शिल्लक (Cash in hand)');
          setBankAccounts(banks);
          setCashAccounts(cash);
          
          if (cash.length > 0) {
            setPaymentData(prev => ({ ...prev, account_id: cash[0].id }));
          }
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchBaseData();
  }, [activeBranch]);

  const fetchStatement = async () => {
    if (!supplier) return setError('कृपया व्यापारी निवडा');
    if (!startDate || !endDate) return setError('कालावधी निवडा');
    
    setLoading(true);
    setError('');
    try {
      const url = `/api/account-statement?supplier_id=${supplier.id}&start_date=${startDate}&end_date=${endDate}`;
      const response = await apiFetch(url);
      if (response.ok) {
        const data = await response.json();
        setStatementData(data);
      } else {
        const errData = await response.json().catch(() => ({}));
        setError(errData.error || 'माहिती मिळवण्यात अडचण आली.');
      }
    } catch (err) {
      setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (supplier) {
      fetchStatement();
    } else {
      setStatementData(null);
    }
  }, [supplier, startDate, endDate, activeBranch]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${d}-${m}-${y}`;
  };

  const handleOpenPayment = () => {
    if (!statementData || !statementData.account) {
        setError('आधी व्यापारी निवडा.');
        return;
    }
    setPaymentData({ 
        date: getTodayDate(), 
        amount: '', 
        payment_method: 'cash', 
        account_id: cashAccounts.length > 0 ? cashAccounts[0].id : '',
        remark: '' 
    });
    setDrawerOpen(true);
  };

  const handleSavePayment = async () => {
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
        setError('कृपया योग्य रक्कम भरा.');
        return;
    }
    if (!paymentData.account_id) {
        setError('कृपया पेमेंट खाते (कॅश/बँक) निवडा.');
        return;
    }

    setLoading(true);
    setError('');

    const payload = {
        date: paymentData.date,
        voucher_type: 'Payment',
        narration: paymentData.remark || `पेमेंट दिले - ${supplier.name}`,
        entries: [
            {
                account_id: statementData.account.id, // Debit Supplier (Decrease Liability)
                debit: parseFloat(paymentData.amount)
            },
            {
                account_id: paymentData.account_id, // Credit Cash/Bank (Decrease Asset)
                credit: parseFloat(paymentData.amount)
            }
        ]
    };

    try {
        const response = await apiFetch('/api/journal-entries', {
            method: 'POST',
            body: JSON.stringify(payload)
        });

        if (response.ok) {
            setDrawerOpen(false);
            fetchStatement(); // Refresh ledger
        } else {
            const errData = await response.json().catch(() => ({}));
            setError(errData.error || errData.message || 'पेमेंट सेव्ह करताना अडचण आली.');
        }
    } catch (err) {
        setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
    } finally {
        setLoading(false);
    }
  };

  let runningBalance = statementData ? parseFloat(statementData.opening_balance || 0) : 0;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f6f8' }} className="ledger-container">
      <PrintHeader tenant={tenant} branch={activeBranch} supplier={supplier} startDate={formatDate(startDate)} endDate={formatDate(endDate)} />
      
      <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #E65100' }} className="no-print">
        <Typography variant="h5" sx={{ color: '#E65100', fontWeight: 'bold' }}>व्यापारी व्यवहार (Supplier Ledger)</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" size="small" startIcon={<PaymentIcon />} onClick={handleOpenPayment} sx={{ backgroundColor: '#E65100', fontWeight: 'bold' }}>
            पेमेंट करा (Payment)
          </Button>
          <Button
            variant="outlined" size="small" startIcon={<PictureAsPdfIcon />}
            onClick={() => window.print()}
            sx={{ borderColor: '#E65100', color: '#E65100', fontWeight: 'bold' }}
            disabled={!statementData}
          >
            खाते उतारा (Print)
          </Button>
        </Box>
      </Box>

      {/* SEARCH BAR */}
      <Paper sx={{ m: 2, p: 2, borderRadius: 2 }} className="no-print">
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ fontWeight: 'bold', minWidth: 60, color: '#424242' }}>व्यापारी:</Typography>
              <Autocomplete
                fullWidth size="small"
                options={suppliers}
                getOptionLabel={(option) => option.name}
                value={supplier}
                onChange={(_, newValue) => setSupplier(newValue)}
                renderInput={(params) => <TextField {...params} placeholder="व्यापारी निवडा..." />}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Typography sx={{ fontWeight: 'bold', minWidth: 60, color: '#424242' }}>कालावधी:</Typography>
              <TextField type="date" size="small" fullWidth value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              <Typography sx={{ color: '#757575' }}>ते</Typography>
              <TextField type="date" size="small" fullWidth value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ m: 2 }} className="no-print">{error}</Alert>}

      {/* LEDGER TABLE */}
      <Box sx={{ m: 2 }}>
        <Paper sx={{ p: 2, borderRadius: 2, overflowX: 'auto', boxShadow: 'none', border: '1px solid #e0e0e0' }} className="print-no-shadow">
          {loading && !statementData ? (
             <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : statementData ? (
             <div className="professional-table-wrapper">
               <table className="professional-table">
               <thead>
                 <tr>
                   <th style={{ width: '12%' }}>दिनांक</th>
                   <th style={{ width: '40%' }}>तपशील (Particulars)</th>
                   <th style={{ width: '16%' }}>नावे (Debit) ₹</th>
                   <th style={{ width: '16%' }}>जमा (Credit) ₹</th>
                   <th style={{ width: '16%' }}>शिल्लक (Balance) ₹</th>
                 </tr>
               </thead>
               <tbody>
                 {/* Opening Balance Row */}
                 <tr style={{ backgroundColor: '#fff8e1' }}>
                   <td>{formatDate(startDate)}</td>
                   <td style={{ fontWeight: 'bold' }}>आरंभीची शिल्लक (Opening Balance)</td>
                   <td align="right">-</td>
                   <td align="right">-</td>
                   <td align="right" style={{ fontWeight: 'bold' }}>
                     {runningBalance < 0 ? `${Math.abs(runningBalance).toFixed(2)} Cr` : `${runningBalance.toFixed(2)} Dr`}
                   </td>
                 </tr>

                 {statementData.entries.map((entry, idx) => {
                   const debit = parseFloat(entry.debit_amount) || 0;
                   const credit = parseFloat(entry.credit_amount) || 0;
                   runningBalance += (debit - credit);

                   return (
                     <tr key={idx}>
                       <td>{formatDate(entry.voucher.date)}</td>
                       <td>
                         <Typography variant="body2" sx={{ fontWeight: 500 }}>{entry.voucher.narration}</Typography>
                         <Typography variant="caption" sx={{ color: '#757575' }}>Voucher: {entry.voucher.voucher_type} #{entry.voucher.id}</Typography>
                       </td>
                       <td align="right" style={{ color: debit > 0 ? '#D32F2F' : 'inherit' }}>
                         {debit > 0 ? debit.toFixed(2) : '-'}
                       </td>
                       <td align="right" style={{ color: credit > 0 ? '#388E3C' : 'inherit' }}>
                         {credit > 0 ? credit.toFixed(2) : '-'}
                       </td>
                       <td align="right" style={{ fontWeight: 'bold' }}>
                         {runningBalance < 0 ? `${Math.abs(runningBalance).toFixed(2)} Cr` : `${Math.abs(runningBalance).toFixed(2)} Dr`}
                       </td>
                     </tr>
                   );
                 })}
                 {/* Closing Balance Row */}
                 <tr style={{ backgroundColor: '#e3f2fd', borderTop: '2px solid #90caf9' }}>
                   <td colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold' }}>अखेरची शिल्लक (Closing Balance):</td>
                   <td align="right">-</td>
                   <td align="right">-</td>
                   <td align="right" style={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                     {runningBalance < 0 ? `${Math.abs(runningBalance).toFixed(2)} Cr` : `${Math.abs(runningBalance).toFixed(2)} Dr`}
                   </td>
                 </tr>
                </tbody>
              </table>
             </div>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', color: '#757575' }}>
              <Typography>कोणताही डेटा उपलब्ध नाही. कृपया व्यापारी निवडा.</Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* PAYMENT DRAWER */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 450 }, p: 0 } }} className="no-print">
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#E65100', color: '#fff' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>व्यापाऱ्याला पेमेंट करा</Typography>
            <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
          </Box>

          <Box sx={{ p: 3, flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: '#424242' }}>व्यापारी: {supplier?.name}</Typography>
            
            <TextField fullWidth label="तारीख" type="date" size="small" sx={{ mb: 3 }} value={paymentData.date} onChange={(e) => setPaymentData({ ...paymentData, date: e.target.value })} InputLabelProps={{ shrink: true }} />
            
            <TextField fullWidth label="रक्कम (Amount)" type="number" size="small" sx={{ mb: 3 }} value={paymentData.amount} onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            
            <FormControl fullWidth size="small" sx={{ mb: 3 }}>
              <InputLabel>पेमेंट प्रकार</InputLabel>
              <Select value={paymentData.payment_method} label="पेमेंट प्रकार" onChange={(e) => {
                const method = e.target.value;
                setPaymentData({ 
                  ...paymentData, 
                  payment_method: method,
                  account_id: method === 'cash' ? (cashAccounts[0]?.id || '') : (bankAccounts.length === 1 ? bankAccounts[0].id : '')
                });
              }}>
                <MenuItem value="cash">रोख (Cash)</MenuItem>
                <MenuItem value="online">ऑनलाइन / बँक</MenuItem>
              </Select>
            </FormControl>

            {paymentData.payment_method === 'online' && bankAccounts.length > 1 && (
              <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                <InputLabel>बँक खाते निवडा</InputLabel>
                <Select value={paymentData.account_id} label="बँक खाते निवडा" onChange={(e) => setPaymentData({ ...paymentData, account_id: e.target.value })}>
                  {bankAccounts.map(bank => (
                    <MenuItem key={bank.id} value={bank.id}>{bank.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField fullWidth label="रिमार्क / तपशील" size="small" multiline rows={3} placeholder="उदा. गुगल पे केले..." value={paymentData.remark} onChange={(e) => setPaymentData({ ...paymentData, remark: e.target.value })} />
          </Box>

          <Box sx={{ p: 2, borderTop: '1px solid #eee' }}>
            <Button fullWidth variant="contained" size="large" disabled={loading} sx={{ backgroundColor: '#E65100', py: 1.5, fontWeight: 'bold' }} onClick={handleSavePayment}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'पेमेंट सेव्ह करा'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default SupplierTransaction;
